/**
 * Service to fetch loan statistics from the API
 *
 * Usage:
 * // With API key parameter
 * const statistics = await LoanStatisticsService.getLoanStatistics('your-api-key');
 *
 * // Without parameter (uses environment variable REACT_APP_LOAN_STATISTICS_API_KEY)
 * const statistics = await LoanStatisticsService.getLoanStatistics();
 */

export interface PerformanceBreakdown {
  current: number;
  "late-30": number;
  "late-60": number;
  "late-90": number;
  default: number;
}

export interface DataPartners {
  count: number;
  partners: string[];
}

export interface LoanStatisticsData {
  totalLoans: number;
  totalActiveLoans: number;
  totalPrincipal: number;
  portfolioValue: number;
  averageLoanAmount: number;
  medianLoanAmount: number;
  averageInterestRate: number;
  averageLTV: number;
  performanceBreakdown: PerformanceBreakdown;
  averageDefaultRate: number;
  defaultRate: number;
  delinquencyRate: number;
  dataPartners: DataPartners;
}

export interface LoanStatisticsMeta {
  timestamp: string;
  requestId: string;
}

export interface LoanStatisticsResponse {
  success: boolean;
  data: LoanStatisticsData;
  meta: LoanStatisticsMeta;
}

export interface DelinquentLoans {
  late30: number;
  late60: number;
  late90: number;
  total: number;
}

export interface DelinquencyRateData {
  delinquencyRate: number; // decimal (0.0 to 1.0)
  delinquencyRatePercentage: number; // percentage (0-100)
  formatted: string; // string like "0.00%"
  totalLoans: number;
  delinquentLoans: DelinquentLoans;
}

export interface DelinquencyRateResponse {
  success: boolean;
  data: DelinquencyRateData;
}

export interface DefaultRateTrendDataPoint {
  x: number;
  y: number;
  date: string;
}

export interface DefaultRateTrendTimeSeries {
  riskBand: string;
  defaultRate: number;
  dataPoints: DefaultRateTrendDataPoint[];
}

export interface DefaultRateTrendRiskBand {
  riskBand: string;
  totalLoans: number;
  defaultCount: number;
  defaultRate: number;
  totalPrincipal: number;
  defaultPrincipal: number;
}

export interface DefaultRateTrendSummary {
  totalLoans: number;
  totalDefaults: number;
}

export interface DefaultRateTrendData {
  timeframe: string;
  riskBands: DefaultRateTrendRiskBand[];
  weightedAverage90D: number;
  timeSeries: DefaultRateTrendTimeSeries[];
  summary: DefaultRateTrendSummary;
}

export interface DefaultRateTrendResponse {
  success: boolean;
  data: DefaultRateTrendData;
}

export class LoanStatisticsService {
  // Base URL for all API endpoints
  private static readonly BASE_URL = "https://chain.crypsey.com";

  private static getApiEndpoint(): string {
    return `${this.BASE_URL}/api/loans/statistics`;
  }

  private static getDelinquencyRateEndpoint(
    queryParams?: Record<string, string>
  ): string {
    let endpoint = `${this.BASE_URL}/api/loans/delinquency-rate`;

    // Add query parameters if provided
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams(queryParams);
      endpoint += `?${params.toString()}`;
    }

    return endpoint;
  }

  private static getDefaultRateTrendsEndpoint(
    queryParams?: Record<string, string>
  ): string {
    let endpoint = `${this.BASE_URL}/api/loans/default-rate-trends`;

    // Add query parameters if provided
    if (queryParams && Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams(queryParams);
      endpoint += `?${params.toString()}`;
    }

    return endpoint;
  }

  private static getApiKey(apiKey?: string): string {
    const key =
      apiKey?.trim() ||
      process.env.REACT_APP_LOAN_STATISTICS_API_KEY?.trim();

    if (!key) {
      throw new Error("API key is required");
    }

    return key;
  }

  /**
   * Get loan statistics from the API
   * @param apiKey - Your API key (customer or data partner key). If not provided, uses environment variable REACT_APP_LOAN_STATISTICS_API_KEY.
   * @returns Promise with loan statistics data
   */
  static async getLoanStatistics(
    apiKey?: string
  ): Promise<LoanStatisticsResponse> {
    const key = this.getApiKey(apiKey);

    try {
      const response = await fetch(this.getApiEndpoint(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": key,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`
        );
      }

      const result: LoanStatisticsResponse = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching loan statistics:", error);
      throw error;
    }
  }

  /**
   * Get delinquency rate from the API
   * @param apiKey - Your API key (customer or data partner key). If not provided, uses environment variable REACT_APP_LOAN_STATISTICS_API_KEY.
   * @param queryParams - Optional query parameters for filtering (e.g., data partner, date range, etc.)
   * @returns Promise with delinquency rate data
   */
  static async getDelinquencyRate(
    apiKey?: string,
    queryParams?: Record<string, string>
  ): Promise<DelinquencyRateResponse> {
    const key = this.getApiKey(apiKey);

    try {
      const endpoint = this.getDelinquencyRateEndpoint(queryParams);
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": key,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`
        );
      }

      const result: DelinquencyRateResponse = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching delinquency rate:", error);
      throw error;
    }
  }

  /**
   * Get default rate trends from the API
   * @param apiKey - Your API key (customer or data partner key). If not provided, uses environment variable REACT_APP_LOAN_STATISTICS_API_KEY.
   * @param queryParams - Optional query parameters for filtering (e.g., timeframe, province, city, etc.)
   * @returns Promise with default rate trends data
   */
  static async getDefaultRateTrends(
    apiKey?: string,
    queryParams?: Record<string, string>
  ): Promise<DefaultRateTrendResponse> {
    const key = this.getApiKey(apiKey);

    try {
      const endpoint = this.getDefaultRateTrendsEndpoint(queryParams);
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": key,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`
        );
      }

      const result: DefaultRateTrendResponse = await response.json();
      return result;
    } catch (error) {
      console.error("Error fetching default rate trends:", error);
      throw error;
    }
  }
}
